import { createContext, PropsWithChildren, useContext } from "react";
import { DevicesReducerTypes, useDevices } from "./DevicesProvider";
import { useServerCommunication } from "./ServerCommunicaton";
import { Device, DeviceState } from "./types";

// CONTEXT
const DeviceContext = createContext<DeviceState>({} as DeviceState);

export const DeviceProvider = ({ unitid, children }: PropsWithChildren<{ unitid: number }>) => {
  const {
    state: { devices },
    dispatch,
  } = useDevices();
  const device = devices.get(unitid) as Device;
  const { getUnitDetails, getUnitTemplate, getUnitConfig, getUnitConfigHistory } = useServerCommunication();

  const init = () => {
    console.log("deviceProvider init");
    dispatch({ type: DevicesReducerTypes.CREATE_DEVICE, payload: { unitid } });
  };

  const updateUnitDetails = () => {
    return getUnitDetails(unitid)
      .then((unitDetails) => {
        dispatch({ type: DevicesReducerTypes.SET_UNIT_DETAILS, payload: { unitid, unitDetails } });
      })
      .catch((err: any) => {
        if (err.status === 400 || err.status === 404) {
          dispatch({ type: DevicesReducerTypes.SET_UNIT_DETAILS, payload: { unitid, unitDetails: null, primaryUnitid: err.data?.primaryUnitid } });
        }
      });
  };

  const updateTemplate = () => {
    return getUnitTemplate(unitid).then((template) => {
      dispatch({ type: DevicesReducerTypes.SET_TEMPLATE, payload: { unitid, template } });
    });
  };

  const updateConfig = (force?: boolean) => {
    return getUnitConfig(unitid)
      .then((config) => {
        dispatch({ type: DevicesReducerTypes.SET_CONFIG, payload: { unitid, config: config || null, force } });
      })
      .catch((err) => console.error(err));
  };

  const updateConfigHistory = () => {
    return getUnitConfigHistory(unitid)
      .then((configHistory) => {
        dispatch({ type: DevicesReducerTypes.SET_CONFIG_HISTORY, payload: { unitid, configHistory } });
      })
      .catch((err) => console.error(err));
  };

  const setCurrentValue = (currentValue: any) => {
    if (!currentValue) {
      return;
    }
    dispatch({ type: DevicesReducerTypes.SET_CURRENT_VALUE, payload: { unitid, currentValue } });
  };

  const setSaving = (saving: boolean) => {
    dispatch({ type: DevicesReducerTypes.SET_LOCAL_VALUE, payload: { unitid, saving } });
  };

  const setSyncLoading = (syncLoading: boolean) => {
    dispatch({ type: DevicesReducerTypes.SET_LOCAL_VALUE, payload: { unitid, syncLoading } });
  };

  const setEmptyConfig = (emptyConfig: any, key?: string) => {
    if (key) {
      dispatch({ type: DevicesReducerTypes.SET_EMPTY_CONFIG, payload: { unitid, key, emptyConfig } });
    } else {
      dispatch({ type: DevicesReducerTypes.SET_CONFIG, payload: { unitid, config: { toolboxConfig: emptyConfig } } });
    }
  };

  return (
    <DeviceContext.Provider
      value={{ device, init, updateUnitDetails, updateTemplate, updateConfig, updateConfigHistory, setCurrentValue, setSaving, setSyncLoading, setEmptyConfig }}
    >
      {children}
    </DeviceContext.Provider>
  );
};

export const useDevice = () => {
  const deviceState = useContext(DeviceContext);
  if (deviceState === null) {
    throw new Error("useDevice must be used within an DeviceProvider");
  }

  return deviceState;
};
